import * as React from 'react';

import { enumBrokeredOrderStatus } from '@rematter/frontend-lib-api-client';
import { Button } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { Trash2 } from 'lucide-react';

import {
  $closeBrokeredOrder,
  $openBrokeredOrder,
  $voidBrokeredOrder,
} from '../../_state/brokeredOrderDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  BrokeredOrderDetailRecord,
  BrokeredOrderRecord,
} from '../../types/brokeredOrder';

export type BrokeredOrderActionHeaderProps = {
  order: BrokeredOrderRecord | BrokeredOrderDetailRecord;
  onRefetch?: () => void;
};

export const BrokeredOrderActionHeader: React.FC<
  BrokeredOrderActionHeaderProps
> = ({ order, onRefetch }) => {
  const t = useTranslations();
  const toast = useToast();

  const openOrder = useSetAtom($openBrokeredOrder);
  const closeOrder = useSetAtom($closeBrokeredOrder);
  const voidOrder = useSetAtom($voidBrokeredOrder);

  const isDraft = order.status === enumBrokeredOrderStatus.Draft;
  const isOpen = order.status === enumBrokeredOrderStatus.Open;
  const isInProgress = order.status === enumBrokeredOrderStatus.InProgress;
  const isClosed = order.status === enumBrokeredOrderStatus.Closed;
  const isFulfilled = order.status === enumBrokeredOrderStatus.Fulfilled;
  const isVoided = order.status === enumBrokeredOrderStatus.Voided;

  const showOpenButton = isDraft;
  const showVoidButton = isDraft || isOpen || isInProgress;
  const showFinishButton = isOpen;
  const showRevertToOpenButton = isClosed || isFulfilled || isVoided;

  const handleOpen = React.useCallback(async () => {
    try {
      await openOrder({ id: order.id });
      toast.success(t.orders.toast.opened);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.orders.toast.openError(
          err instanceof Error ? err.message : t.common.errors.unknown,
        ),
      );
    }
  }, [order.id, openOrder, toast, t, onRefetch]);

  const handleVoid = React.useCallback(async () => {
    try {
      await voidOrder({ id: order.id });
      toast.success(t.orders.toast.voided);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.orders.toast.voidError(
          err instanceof Error ? err.message : t.common.errors.unknown,
        ),
      );
    }
  }, [order.id, voidOrder, toast, t, onRefetch]);

  const handleFinish = React.useCallback(async () => {
    try {
      await closeOrder({ id: order.id });
      toast.success(t.orders.toast.finished);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.orders.toast.finishError(
          err instanceof Error ? err.message : t.common.errors.unknown,
        ),
      );
    }
  }, [order.id, closeOrder, toast, t, onRefetch]);

  const handleRevertToOpen = React.useCallback(async () => {
    try {
      await openOrder({ id: order.id });
      toast.success(t.orders.toast.reverted);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.orders.toast.revertError(
          err instanceof Error ? err.message : t.common.errors.unknown,
        ),
      );
    }
  }, [order.id, openOrder, toast, t, onRefetch]);

  if (
    !showOpenButton &&
    !showVoidButton &&
    !showFinishButton &&
    !showRevertToOpenButton
  ) {
    return null;
  }

  return (
    <div className="@BrokeredOrderActionHeader flex items-center gap-2">
      {showRevertToOpenButton && (
        <Button variant="outline" onPress={handleRevertToOpen}>
          {t.orders.detail.actions.revertToOpen}
        </Button>
      )}
      {showVoidButton && (
        <Button
          variant="outline"
          color="failure"
          onPress={handleVoid}
          icon={<Trash2 className="size-4" />}
        >
          {t.orders.detail.actions.voidOrder}
        </Button>
      )}
      {showFinishButton && (
        <Button variant="solid" color="primary" onPress={handleFinish}>
          {t.orders.detail.actions.finishOrder}
        </Button>
      )}
      {showOpenButton && (
        <Button variant="solid" color="primary" onPress={handleOpen}>
          {t.orders.detail.actions.openOrder}
        </Button>
      )}
    </div>
  );
};
